import React, {Fragment, useEffect, useState} from "react";
import { Form, Row, Col } from "react-bootstrap";
import { Alert, Button, CircularProgress, SvgIcon } from "@mui/material";
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from "@hookform/resolvers/yup";
import { object, string } from 'yup';
import { TextInput } from "../../../Helpers/Inputs";
import ShieldCheckmarkIcon from '../../../../icons/shield-checkmark.svg?react';
import { SaveQuoteLog } from "./SaveQuoteLog";
import { emptyStringToUndefined } from "../../../Helpers/Formatters/EmptyStringToUndefined";
import { getBasicInfo, getSessionStorageItem, SESSION_STORAGE_SAVED_QUOTE_DATA, setBasicInfo, } from '../../../../sessionStorage';
import './SaveQuoteButton.scss';

export default function SaveQuoteAnalysis(props) {
    
    const [email, setEmail] = useState(null);
    const [firstName, setFirstName] = useState(null);
    const [lastName, setLastName] = useState(null);
    const [isSaveloading, setSaveLoading] = useState(false);
    const basicInfo = getBasicInfo();
    const saveQuoteData = getSessionStorageItem(SESSION_STORAGE_SAVED_QUOTE_DATA);
    
    function handleClick() {
        props.fields.setErrorMessage({ status: '', message: '' });
        props.fields.setModalSaveQuoteIsOpen(false);
    };

    const formMethods = useForm({
        resolver: yupResolver(SaveQuoteAnalysis.ValidationSchema),
        mode: 'all',
        reValidateMode: 'all',
        defaultValues: {
            firstName: "",
            email: "",
            isQuoteSaved: props.isQuoteSaved, // Required for `when` condition to work
          },
    });
    
    const { handleSubmit, trigger } = formMethods;

    const newSaveQuoteData = {...props.saveQuoteData};

    useEffect(() => {
        if (saveQuoteData && !!saveQuoteData.quoteSaved) {
            setFirstName(saveQuoteData.firstName);
            setLastName(null);
            setEmail(saveQuoteData.emailAddress);
        }
    }, [props.saveQuoteData, props.isQuoteSaved]);

    useEffect(() => {
        basicInfo.email = email;
        basicInfo.firstName = firstName;
        basicInfo.lastName = !!lastName ? lastName : null;
        newSaveQuoteData.emailAddress = email;
        newSaveQuoteData.firstName = firstName;
        newSaveQuoteData.lastName = !!lastName ? lastName : null;
        const contact = {
            firstName: newSaveQuoteData.firstName,
            lastName: newSaveQuoteData.lastName,
            emailAddress: newSaveQuoteData.emailAddress,
        }
        setBasicInfo(basicInfo);
        props.handleContactChange(contact);
    }, [email, firstName, lastName]);


    const handleValueChange = (field, value) =>{
        if (field === "email") {
            setEmail(value);
        }
        if (field === "firstName") {
            const singleSpaceValue = value.replace(/\s+/g, " ");
            setFirstName(singleSpaceValue);
        }
        if (field === "lastName") {
            const singleSpaceValue = value.replace(/\s+/g, " ");
            setLastName(singleSpaceValue);
        }
        trigger(field);
    };

    const handleSaveQuote = () => {
        setSaveLoading(true);
        props.fields.setErrorMessage({ status: '', message: '' });
        props.setEmailPrefill(basicInfo);
        SaveQuoteLog(newSaveQuoteData, setSaveLoading, props.fields.setErrorMessage, props.setQuoteSaved, props.saveMessage);  
    };

    return (
        <React.Fragment>
            <Row>
                <Col md={12}>
                    <p className={props.fields.errorMessage.status === "success" ? "save-term-text center" : "save-term-text" }>{props.fields.errorMessage.status === "success" ? props.saveMessage && props.saveMessage.success.message : props.saveMessage && props.saveMessage.initial.message}</p>
                    <FormProvider {...formMethods}>
                    <Form onSubmit={handleSubmit(handleSaveQuote)}>
                    {!props.fields.errorMessage.status && 
                    <Fragment>
                        {!props.isQuoteSaved && 
                        <Fragment>
                            <div className="mb-3">
                                <TextInput
                                    name="firstName"
                                    id="firstName"
                                    label="First Name"
                                    value={firstName}
                                    onChange={(e) => handleValueChange("firstName", e.target.value)}
                                />
                            </div>
                            <div className="mb-3">
                                <TextInput
                                    name="email"
                                    id="email"
                                    label="Email"
                                    value={email}
                                    onChange={(e) => handleValueChange("email", e.target.value)}
                                />
                            </div>
                        </Fragment>
                        }
                        <Button
                            variant="contained"
                            color='primary'
                            size="large"
                            className='btn-wide-sm mb-2'
                            fullWidth={true}
                            type="submit"
                            disabled={props.fields.loading || isSaveloading}
                        >
                        {isSaveloading ? <CircularProgress size={25} /> :
                        <React.Fragment>
                            <SvgIcon component={ShieldCheckmarkIcon} className="me-2" /> <b>{props.isQuoteSaved  ? "UPDATE SAVED QUOTE" : "SAVE MY QUOTE"}</b>
                        </React.Fragment>
                        }
                        </Button>
                    </Fragment>
                    }
                    </Form>
                    </FormProvider>

                    { props.fields.errorMessage.message && 
                    <Row className="mt-2">
                        <Col >
                        { props.fields.errorMessage.status !== "success" &&
                        <Alert severity={props.fields.errorMessage.status} className="mb-2">
                            <div> {props.fields.errorMessage.message}</div>
                        </Alert>
                        }
                        </Col>
                    </Row>}
                    <Button
                        variant="outlined"
                        color='primary'
                        size="large"
                        onClick={() => handleClick()}
                        className={`btn-wide-sm save-quote-button-white`}
                        fullWidth={true}
                    >
                        <b>{props.fields.errorMessage.status === "success" ? "BACK TO MY QUOTES" : props.isQuoteSaved ? "CONTINUE WITHOUT UPDATING" : "CONTINUE WITHOUT SAVING"}</b>
                    </Button>
                    { !props.isQuoteSaved && 
                        <p className="privacy-info">Your information is secure and confidential. We never sell your personal information.</p>
                    }
                </Col>
            </Row>
        </React.Fragment >
    )
}

SaveQuoteAnalysis.ValidationSchema = object().shape({
    firstName: string().transform(emptyStringToUndefined).when('isQuoteSaved', {
        is: true,
        then: (schema) => schema.notRequired(),
        otherwise: (schema) => schema
            .required('First name is required'),
    }),
    email: string().transform(emptyStringToUndefined).when('isQuoteSaved', {
        is: true,
        then: (schema) => schema.notRequired(),
        otherwise: (schema) => schema
            .email('Enter a valid email')
            .required('Email is required'),
    }),
});
